import type { LucideIcon } from "lucide-react";
import {
  BadgeCheck,
  Box,
  CalendarRange,
  DraftingCompass,
  Factory,
  FolderKanban,
  Settings2,
  ShoppingCart,
  Truck,
  Warehouse,
} from "lucide-react";
import type { ParseKeys } from "i18next";

// Single source for the module menu: the sidebar, the ⌘K palette, breadcrumbs,
// the status bar's session code, /s/<session> links and the placeholder route
// all read from here. Order follows the roadmap in CLAUDE.md. Finance is out
// of scope and deliberately absent.
//
// Session codes (Patronix scheme, one per screen): PX-<MODULE>-<NNN>, e.g.
// PX-WHS-110. MODULE is a language-neutral 3-letter module code; the hundreds
// digit is the menu group inside the module (1xx first group, 2xx second…;
// 0xx the module's overview: its manager dashboard),
// the tens the screen inside the group; units stay free for related screens.
// Codes never change once shipped — users quote them in support requests and
// type them into search (with or without "PX" and dashes: "whs110").

export type ModuleTone =
  | "master"
  | "sales"
  | "procurement"
  | "warehousing"
  | "engineering"
  | "manufacturing"
  | "planning"
  | "quality"
  | "project"
  | "settings";

export interface NavItem {
  /** URL segment under the module, e.g. "purchase-orders". */
  slug: string;
  labelKey: ParseKeys;
  /** Session code, unique, e.g. "PX-PUR-110". */
  session: string;
  /** Menu group inside the module (second menu level). */
  group: ParseKeys;
  /** True once the screen exists; otherwise the placeholder route renders. */
  built?: boolean;
  /** Route of a screen that lives outside its module's URL space (e.g. "/process-map"). */
  path?: string;
  /** False: reachable by session code, ⌘K and links, but not listed in the module menu. */
  menu?: false;
  /** A menu link to another module's screen (same session and path as that
   *  screen); lookups by session or path resolve to the screen's own module. */
  shortcut?: true;
}

export interface NavModule {
  /** URL segment, e.g. "procurement". */
  slug: string;
  labelKey: ParseKeys;
  icon: LucideIcon;
  tone: ModuleTone;
  /** Module code, the prefix of its session codes, e.g. "PX-WHS". */
  menu: string;
  items: NavItem[];
}

export const HOME_SESSION = "PX-HOM-000";
export const PROCESS_MAP_PATH = "/process-map";

export const modules: NavModule[] = [
  {
    // Items and partners (ADR-048: the structures and process data of each
    // department stay in that department's module, in its "Master data" group).
    slug: "master-data",
    labelKey: "nav.masterData.title",
    icon: Box,
    tone: "master",
    menu: "PX-MDT",
    items: [
      { slug: "items", labelKey: "nav.masterData.items", session: "PX-MDT-110", group: "nav.groups.items", built: true },
      { slug: "units", labelKey: "nav.masterData.units", session: "PX-MDT-120", group: "nav.groups.items", built: true },
      { slug: "lots", labelKey: "nav.masterData.lots", session: "PX-MDT-130", group: "nav.groups.items", built: true },
      { slug: "serials", labelKey: "nav.masterData.serials", session: "PX-MDT-140", group: "nav.groups.items", built: true },
      { slug: "partners", labelKey: "nav.masterData.partners", session: "PX-MDT-210", group: "nav.groups.partners", built: true },
    ],
  },
  {
    slug: "sales",
    labelKey: "nav.sales.title",
    icon: ShoppingCart,
    tone: "sales",
    menu: "PX-SAL",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-SAL-010", group: "nav.groups.overview", built: true },
      { slug: "orders", labelKey: "nav.sales.orders", session: "PX-SAL-110", group: "nav.groups.orders", built: true },
      { slug: "returns", labelKey: "nav.sales.returns", session: "PX-SAL-120", group: "nav.groups.orders", built: true },
      { slug: "picking", labelKey: "nav.sales.picking", session: "PX-SAL-210", group: "nav.groups.outbound", built: true },
      { slug: "shipments", labelKey: "nav.sales.shipments", session: "PX-SAL-220", group: "nav.groups.outbound", built: true },
      { slug: "customers", labelKey: "nav.sales.customers", session: "PX-SAL-310", group: "nav.groups.masterData", built: true },
      { slug: "price-lists", labelKey: "nav.sales.priceLists", session: "PX-SAL-320", group: "nav.groups.masterData", built: true },
      { slug: "discounts", labelKey: "nav.sales.discounts", session: "PX-SAL-330", group: "nav.groups.masterData", built: true },
    ],
  },
  {
    slug: "procurement",
    labelKey: "nav.procurement.title",
    icon: Truck,
    tone: "procurement",
    menu: "PX-PUR",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-PUR-010", group: "nav.groups.overview", built: true },
      { slug: "purchase-orders", labelKey: "nav.procurement.purchaseOrders", session: "PX-PUR-110", group: "nav.groups.orders", built: true },
      { slug: "supplier-returns", labelKey: "nav.procurement.supplierReturns", session: "PX-PUR-120", group: "nav.groups.orders", built: true },
      { slug: "receipts", labelKey: "nav.procurement.receipts", session: "PX-PUR-210", group: "nav.groups.inbound", built: true },
      { slug: "receiving", labelKey: "nav.procurement.receiving", session: "PX-PUR-220", group: "nav.groups.inbound", built: true },
      { slug: "suppliers", labelKey: "nav.procurement.suppliers", session: "PX-PUR-310", group: "nav.groups.masterData", built: true },
      { slug: "purchase-price-lists", labelKey: "nav.procurement.purchasePriceLists", session: "PX-PUR-320", group: "nav.groups.masterData", built: true },
    ],
  },
  {
    slug: "warehousing",
    labelKey: "nav.warehousing.title",
    icon: Warehouse,
    tone: "warehousing",
    menu: "PX-WHS",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-WHS-010", group: "nav.groups.overview", built: true },
      { slug: "inventory", labelKey: "nav.warehousing.inventory", session: "PX-WHS-110", group: "nav.groups.inventory", built: true },
      { slug: "warehouse-orders", labelKey: "nav.warehousing.warehouseOrders", session: "PX-WHS-120", group: "nav.groups.inventory", built: true },
      { slug: "adjustments", labelKey: "nav.warehousing.adjustments", session: "PX-WHS-210", group: "nav.groups.inventoryChange", built: true },
      { slug: "transfers", labelKey: "nav.warehousing.transfers", session: "PX-WHS-220", group: "nav.groups.inventoryChange", built: true },
      { slug: "cycle-counts", labelKey: "nav.warehousing.cycleCounts", session: "PX-WHS-230", group: "nav.groups.inventoryChange", built: true },
      // The Quality screen, listed here too: receipt inspections block stock (ADR-046).
      { slug: "inspection-orders", labelKey: "nav.quality.inspectionOrders", session: "PX-QMS-110", group: "nav.groups.inventoryChange", built: true,
        path: "/quality/inspection-orders", shortcut: true },
      { slug: "warehouses", labelKey: "nav.warehousing.warehouses", session: "PX-WHS-310", group: "nav.groups.masterData", built: true },
      { slug: "locations", labelKey: "nav.warehousing.locations", session: "PX-WHS-320", group: "nav.groups.masterData", built: true },
      { slug: "adjustment-reasons", labelKey: "nav.warehousing.adjustmentReasons", session: "PX-WHS-330", group: "nav.groups.masterData", built: true },
    ],
  },
  {
    // Product structures as engineering and production see them (ADR-043 / ADR-044):
    // EBOM → MBOM → PBOM, then the structure analysis and engineering changes.
    // Routings and work centers stay in Manufacturing (process and capacity data).
    slug: "engineering",
    labelKey: "nav.engineering.title",
    icon: DraftingCompass,
    tone: "engineering",
    menu: "PX-ENG",
    items: [
      { slug: "engineering-boms", labelKey: "nav.engineering.engineeringBoms", session: "PX-ENG-110", group: "nav.groups.productStructures", built: true },
      { slug: "boms", labelKey: "nav.engineering.boms", session: "PX-ENG-120", group: "nav.groups.productStructures", built: true },
      { slug: "production-boms", labelKey: "nav.engineering.productionBoms", session: "PX-ENG-130", group: "nav.groups.productStructures", built: true },
      { slug: "bom-structure", labelKey: "nav.engineering.bomStructure", session: "PX-ENG-210", group: "nav.groups.analysis", built: true },
      { slug: "changes", labelKey: "nav.engineering.changes", session: "PX-ENG-310", group: "nav.groups.changes", built: true },
    ],
  },
  {
    slug: "manufacturing",
    labelKey: "nav.manufacturing.title",
    icon: Factory,
    tone: "manufacturing",
    menu: "PX-MFG",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-MFG-010", group: "nav.groups.overview", built: true },
      { slug: "production-orders", labelKey: "nav.manufacturing.productionOrders", session: "PX-MFG-110", group: "nav.groups.orders", built: true },
      { slug: "kits", labelKey: "nav.manufacturing.kits", session: "PX-MFG-120", group: "nav.groups.orders", built: true },
      { slug: "workbench", labelKey: "nav.manufacturing.workbench", session: "PX-MFG-130", group: "nav.groups.orders", built: true },
      { slug: "routings", labelKey: "nav.manufacturing.routings", session: "PX-MFG-220", group: "nav.groups.processData", built: true },
      { slug: "work-centers", labelKey: "nav.manufacturing.workCenters", session: "PX-MFG-310", group: "nav.groups.masterData", built: true },
    ],
  },
  {
    slug: "planning",
    labelKey: "nav.planning.title",
    icon: CalendarRange,
    tone: "planning",
    menu: "PX-PLN",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-PLN-010", group: "nav.groups.overview", built: true },
      { slug: "mrp", labelKey: "nav.planning.mrp", session: "PX-PLN-110", group: "nav.groups.mrp", built: true },
      { slug: "planned-orders", labelKey: "nav.planning.plannedOrders", session: "PX-PLN-120", group: "nav.groups.mrp", built: true },
      { slug: "suggestions", labelKey: "nav.planning.suggestions", session: "PX-PLN-130", group: "nav.groups.mrp", built: true },
      { slug: "master-plan", labelKey: "nav.planning.masterPlan", session: "PX-PLN-140", group: "nav.groups.mrp", built: true },
      { slug: "stock-coverage", labelKey: "nav.planning.stockCoverage", session: "PX-PLN-210", group: "nav.groups.analysis", built: true },
    ],
  },
  {
    slug: "quality",
    labelKey: "nav.quality.title",
    icon: BadgeCheck,
    tone: "quality",
    menu: "PX-QMS",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-QMS-010", group: "nav.groups.overview", built: true },
      { slug: "inspection-orders", labelKey: "nav.quality.inspectionOrders", session: "PX-QMS-110", group: "nav.groups.inspections", built: true },
      { slug: "nonconformances", labelKey: "nav.quality.nonconformances", session: "PX-QMS-120", group: "nav.groups.inspections", built: true },
      { slug: "inspection-plans", labelKey: "nav.quality.inspectionPlans", session: "PX-QMS-210", group: "nav.groups.masterData", built: true },
      { slug: "characteristics", labelKey: "nav.quality.characteristics", session: "PX-QMS-220", group: "nav.groups.masterData", built: true },
    ],
  },
  {
    slug: "project",
    labelKey: "nav.project.title",
    icon: FolderKanban,
    tone: "project",
    menu: "PX-PRJ",
    items: [
      { slug: "dashboard", labelKey: "nav.dashboard", session: "PX-PRJ-010", group: "nav.groups.overview", built: true },
      { slug: "projects", labelKey: "nav.project.projects", session: "PX-PRJ-110", group: "nav.groups.projects", built: true },
      { slug: "progress", labelKey: "nav.project.progress", session: "PX-PRJ-120", group: "nav.groups.projects", built: true },
      { slug: "my-time", labelKey: "nav.project.myTime", session: "PX-PRJ-210", group: "nav.groups.time", built: true },
    ],
  },
];

export const settingsModule: NavModule = {
  slug: "settings",
  labelKey: "nav.settingsGroup.title",
  icon: Settings2,
  tone: "settings",
  menu: "PX-SYS",
  items: [
    { slug: "companies", labelKey: "nav.settingsGroup.companies", session: "PX-SYS-110", group: "nav.groups.organization", built: true },
    { slug: "users", labelKey: "nav.settingsGroup.users", session: "PX-SYS-120", group: "nav.groups.organization", built: true },
    { slug: "security", labelKey: "nav.settingsGroup.security", session: "PX-SYS-130", group: "nav.groups.organization", built: true },
    { slug: "agents", labelKey: "nav.settingsGroup.agents", session: "PX-SYS-140", group: "nav.groups.organization", built: true },
    { slug: "numbering", labelKey: "nav.settingsGroup.numbering", session: "PX-SYS-210", group: "nav.groups.system", built: true },
    { slug: "audit-log", labelKey: "nav.settingsGroup.auditLog", session: "PX-SYS-220", group: "nav.groups.system", built: true },
    // Shown under Home in the sidebar (top level); keeps its shipped session code.
    { slug: "process-map", labelKey: "nav.settingsGroup.processMap", session: "PX-SYS-230", group: "nav.groups.system", built: true,
      path: PROCESS_MAP_PATH, menu: false },
  ],
};

export const allModules: NavModule[] = [...modules, settingsModule];

/** Shipped session codes of screens that moved module (codes never change, so
 *  the old one keeps opening the screen): old → current. ADR-044, ADR-048. */
export const sessionAliases: Readonly<Record<string, string>> = {
  "PX-MFG-210": "PX-ENG-120",
  "PX-MFG-230": "PX-ENG-210",
  "PX-MFG-240": "PX-ENG-110",
  // Codes of the short-lived move to Master data (ADR-046, reverted by ADR-048).
  "PX-MDT-310": "PX-ENG-110",
  "PX-MDT-320": "PX-ENG-120",
  "PX-MDT-330": "PX-ENG-130",
  "PX-MDT-410": "PX-MFG-220",
  "PX-MDT-420": "PX-MFG-310",
  "PX-MDT-510": "PX-WHS-310",
  "PX-MDT-520": "PX-WHS-320",
  "PX-MDT-530": "PX-WHS-330",
  "PX-MDT-610": "PX-QMS-210",
  "PX-MDT-620": "PX-QMS-220",
}

export const SESSION_CODE = /^PX-[A-Z]{3}-\d{3}$/;

/** "px-whs-110", "WHS110", " whs 110 " → "WHS110" (compare form: no PX, no separators). */
export function normalizeSession(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/^PX(?=[A-Z]{3})/, "");
}

export function itemPath(module: NavModule, item: NavItem): string {
  return item.path ?? `/${module.slug}/${item.slug}`;
}

export function findNav(
  moduleSlug: string,
  itemSlug: string,
): { module: NavModule; item: NavItem } | undefined {
  const module = allModules.find((m) => m.slug === moduleSlug);
  const item = module?.items.find((i) => i.slug === itemSlug && !i.shortcut);
  return module && item ? { module, item } : undefined;
}

/** The screen of a session code, e.g. "PX-WHS-110", "px-whs-110" or "whs110". */
export function findSession(code: string): { module: NavModule; item: NavItem } | undefined {
  let c = normalizeSession(code);
  if (!c) return undefined;
  const alias = Object.entries(sessionAliases).find(([old]) => normalizeSession(old) === c);
  if (alias) c = normalizeSession(alias[1]);
  for (const module of allModules) {
    const item = module.items.find((i) => !i.shortcut && normalizeSession(i.session) === c);
    if (item) return { module, item };
  }
  return undefined;
}

/** Session code of a pathname ("/warehousing/inventory" → PX-WHS-110; "/" → home). */
export function sessionOfPath(pathname: string): string | null {
  const [, moduleSlug = "", itemSlug = ""] = pathname.split("/");
  if (!moduleSlug) return HOME_SESSION;
  for (const module of allModules) {
    const item = module.items.find((i) => !i.shortcut && i.path === `/${moduleSlug}`);
    if (item) return item.session;
  }
  return findNav(moduleSlug, itemSlug)?.item.session ?? null;
}

/** Items of a module in menu order, grouped by their menu group (first appearance order). */
export function groupedItems(module: NavModule): { group: ParseKeys; items: NavItem[] }[] {
  const out: { group: ParseKeys; items: NavItem[] }[] = [];
  for (const item of module.items) {
    if (item.menu === false) continue;
    const last = out.find((g) => g.group === item.group);
    if (last) last.items.push(item);
    else out.push({ group: item.group, items: [item] });
  }
  return out;
}

/** Tailwind classes for a module's tinted icon tile. Static strings so
 *  Tailwind's scanner can see them. */
export const toneClasses: Record<ModuleTone, string> = {
  master: "bg-(--mod-master-soft) text-(--mod-master-ink)",
  sales: "bg-(--mod-sales-soft) text-(--mod-sales-ink)",
  procurement: "bg-(--mod-procurement-soft) text-(--mod-procurement-ink)",
  warehousing: "bg-(--mod-warehousing-soft) text-(--mod-warehousing-ink)",
  engineering: "bg-(--mod-engineering-soft) text-(--mod-engineering-ink)",
  manufacturing: "bg-(--mod-manufacturing-soft) text-(--mod-manufacturing-ink)",
  planning: "bg-(--mod-planning-soft) text-(--mod-planning-ink)",
  quality: "bg-(--mod-quality-soft) text-(--mod-quality-ink)",
  project: "bg-(--mod-project-soft) text-(--mod-project-ink)",
  settings: "bg-(--mod-settings-soft) text-(--mod-settings-ink)",
};
